import { getCardValue, isMeldableRank, isThree, isWild } from "./card.js";
import { fail, succeed } from "./result.js";
/** A meld reaching this size becomes a Book (§5). */
export const BOOK_SIZE = 7;
/**
 * Naturals required before a meld may become a Book. A meld may not hold more
 * wilds than naturals, so the seventh card can only land legally on top of at
 * least four naturals.
 */
const NATURALS_REQUIRED_FOR_BOOK = 4;
const MINIMUM_MELD_SIZE = 3;
const MINIMUM_MELD_NATURALS = 2;
export function countWilds(cards) {
    return cards.filter(isWild).length;
}
export function countNaturals(cards) {
    return cards.length - countWilds(cards);
}
export function isBook(meld) {
    return meld.cards.length >= BOOK_SIZE;
}
/** A meld holding no wilds; once it becomes a Book its purity is locked (§6). */
export function isPure(meld) {
    return countWilds(meld.cards) === 0;
}
export function isPureBook(meld) {
    return isBook(meld) && isPure(meld);
}
export function isWildBook(meld) {
    return isBook(meld) && !isPure(meld);
}
/** Face value of the cards on the table, excluding any Book bonus. */
export function getMeldValue(meld) {
    return getCardsValue(meld.cards);
}
export function getCardsValue(cards) {
    return cards.reduce((total, card) => total + getCardValue(card), 0);
}
export function findMeld(melds, rank) {
    return melds.find((meld) => meld.rank === rank);
}
/** The rank a set of cards melds at, or undefined when the naturals disagree. */
function resolveRank(cards) {
    let resolved;
    for (const card of cards) {
        if (isWild(card)) {
            continue;
        }
        if (!isMeldableRank(card.rank)) {
            return undefined;
        }
        if (resolved !== undefined && resolved !== card.rank) {
            return undefined;
        }
        resolved = card.rank;
    }
    return resolved;
}
/**
 * Checks the wild-to-natural ratio a set of cards must satisfy to exist as a
 * meld or Book. Sets short of Book size may not hold more wilds than naturals;
 * larger sets must have been able to pass through a legal seven-card Book.
 */
function checkComposition(cards) {
    const naturals = countNaturals(cards);
    const wilds = cards.length - naturals;
    if (cards.length >= BOOK_SIZE) {
        if (naturals < NATURALS_REQUIRED_FOR_BOOK) {
            return fail("meld-too-many-wilds", `a book needs at least ${NATURALS_REQUIRED_FOR_BOOK} natural cards, found ${naturals}`);
        }
        return succeed(null);
    }
    if (wilds > naturals) {
        return fail("meld-too-many-wilds", `a meld cannot hold more wilds (${wilds}) than naturals (${naturals})`);
    }
    return succeed(null);
}
function checkPlayableCards(cards) {
    const seen = new Set();
    for (const card of cards) {
        if (isThree(card)) {
            return fail("meld-unmeldable-rank", "threes cannot be melded");
        }
        if (seen.has(card.id)) {
            return fail("duplicate-card", `card ${card.id} was played twice`);
        }
        seen.add(card.id);
    }
    return succeed(null);
}
/** Builds a brand new meld, validating size, rank agreement and wild ratio (§5). */
export function createMeld(cards) {
    const playable = checkPlayableCards(cards);
    if (!playable.ok) {
        return playable;
    }
    if (cards.length < MINIMUM_MELD_SIZE) {
        return fail("meld-too-small", `a meld needs at least ${MINIMUM_MELD_SIZE} cards, found ${cards.length}`);
    }
    if (countNaturals(cards) < MINIMUM_MELD_NATURALS) {
        return fail("meld-not-enough-naturals", `a meld needs at least ${MINIMUM_MELD_NATURALS} cards of the same rank`);
    }
    const rank = resolveRank(cards);
    if (rank === undefined) {
        return fail("meld-mixed-ranks", "every natural card in a meld must share the same rank");
    }
    const composition = checkComposition(cards);
    if (!composition.ok) {
        return composition;
    }
    return succeed({ rank, cards: sortMeldCards(cards) });
}
/** Extends an existing meld or Book, honoring the pure-Book wild lock (§6). */
export function addToMeld(meld, cards) {
    if (cards.length === 0) {
        return fail("nothing-played", "no cards were added to the meld");
    }
    const playable = checkPlayableCards(cards);
    if (!playable.ok) {
        return playable;
    }
    for (const card of cards) {
        if (!isWild(card) && card.rank !== meld.rank) {
            return fail("meld-mixed-ranks", `a ${card.rank} cannot join the ${meld.rank} meld`);
        }
    }
    const wasPureBook = isPureBook(meld);
    if (wasPureBook && countWilds(cards) > 0) {
        return fail("pure-book-locked", `the ${meld.rank} book is pure and can no longer take wild cards`);
    }
    const combined = [...meld.cards, ...cards];
    if (!isBook(meld)) {
        const composition = checkComposition(combined);
        if (!composition.ok) {
            return composition;
        }
    }
    return succeed({ rank: meld.rank, cards: sortMeldCards(combined) });
}
/** Naturals first, then wilds, so a meld always renders in a stable order. */
function sortMeldCards(cards) {
    return [
        ...cards.filter((card) => !isWild(card)),
        ...cards.filter(isWild)
    ];
}
//# sourceMappingURL=meld.js.map